/*
 * CAPTURE-OWNED MODULE
 * Similar existing fields, shown under the Label input of the Add Field form
 * while the user types (utils/libraryFieldSuggestions). Warns, never blocks:
 * the form's own submit button always creates the custom field, and
 * "Create anyway" here does the same.
 */
import React, { useEffect, useRef, useState } from 'react';
import { DEFAULT_VISIBLE_MATCHES } from '../../utils/fieldMatch';
import { docTypeName } from '../../utils/libraryFieldSuggestions';

const TIER_TEXT = {
  exact: 'Same name',
  contained: 'Shares all words',
  partial: 'Similar',
};

function LibraryRow({ s, onUse, busy, documentType, error }) {
  const name = s.item.label || s.item.key;
  let action = null;
  let state = null;
  if (s.templateState === 'on_template') {
    state = 'On this template';
  } else if (s.templateState === 'on_template_disabled') {
    state = 'On this template, disabled';
    if (s.linked && onUse) {
      action = <button type="button" className="btn btn-xs fm-action" disabled={busy} onClick={() => onUse(s)}>Re-enable</button>;
    }
  } else if (onUse) {
    action = (
      <button type="button" className="btn btn-xs btn-primary fm-action" disabled={busy} onClick={() => onUse(s)}>
        {s.needsLink ? `Link to ${docTypeName(documentType)} and use` : 'Use library field'}
      </button>
    );
  }
  const usedBy = !s.linked && s.usedBy.length > 0
    ? `Standard field, used by ${s.usedBy.map(docTypeName).join(', ')}`
    : null;
  return (
    <li className="fm-row" data-key={s.item.key}>
      <div className="fm-row-text">
        <span className="fm-row-name">{name}</span>{' '}
        <span className="fm-row-key">{s.item.key}</span>{' '}
        <span className={`fm-row-tier fm-tier-${s.tier}`}>{TIER_TEXT[s.tier]}</span>
        {state && <div className="fm-row-state">{state}</div>}
        {usedBy && <div className="fm-row-usedby">{usedBy}</div>}
      </div>
      {action}
      {error && <div className="fm-row-error" role="alert">{error}</div>}
    </li>
  );
}

/**
 * @param {object}   props
 * @param {object}   props.suggestions   findFieldSuggestions(...) result, or null
 * @param {Function} [props.onUseLibraryField]  (row, { needsLink, label }) => Promise<{ ok, message? }> | void;
 *                                             absent hides the actions. While it runs every row button
 *                                             is disabled (one click); { ok: false, message } shows on the row.
 * @param {Function} props.onCreateAnyway
 * @param {boolean}  [props.confirmPending]  Save was pressed once on an exact or duplicate match
 * @param {boolean}  [props.busy]
 * @param {string}   [props.documentType]  the template's type key, for "Link to <type> and use"
 */
export default function FieldMatchSuggestions({ suggestions, onUseLibraryField, onCreateAnyway, confirmPending = false, busy = false, documentType = '' }) {
  const [showAll, setShowAll] = useState(false);
  const [pendingKey, setPendingKey] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const use = onUseLibraryField ? async (s) => {
    if (pendingKey) return; // one click: a second press while the first runs does nothing
    const key = s.item.key;
    setPendingKey(key);
    setRowErrors((e) => ({ ...e, [key]: null }));
    try {
      const result = await onUseLibraryField(s.row, { needsLink: Boolean(s.needsLink), label: s.item.label || key });
      if (result && result.ok === false && mounted.current) {
        setRowErrors((e) => ({ ...e, [key]: result.message || 'Could not use this field.' }));
      }
    } catch (err) {
      if (mounted.current) setRowErrors((e) => ({ ...e, [key]: err?.message || 'Could not use this field.' }));
    } finally {
      if (mounted.current) setPendingKey(null);
    }
  } : null;

  if (!suggestions) return null;

  if (suggestions.attribute) {
    return (
      <div className="fm-panel" data-testid="field-match">
        <div className="fm-attribute">
          Document type is a document attribute. It is shown at the top of the field list and is not
          read from a zone, so it is not added as a field.
        </div>
      </div>
    );
  }

  const { templateDuplicates, library } = suggestions;
  if (templateDuplicates.length === 0 && library.length === 0) return null;
  const visible = showAll ? library : library.slice(0, DEFAULT_VISIBLE_MATCHES);

  return (
    <div className="fm-panel" data-testid="field-match">
      {templateDuplicates.map(({ field }) => (
        <div key={field.id || field.field_key} className="fm-duplicate" role="alert">
          This template already has a field &quot;{field.label || field.field_key}&quot; ({field.field_key})
          {field.enabled === false ? ', disabled.' : '.'}
        </div>
      ))}
      {library.length > 0 && (
        <>
          <div className="fm-heading">Similar fields in the Field Library</div>
          <ul className="fm-list">
            {visible.map((s) => (
              <LibraryRow
                key={s.item.key}
                s={s}
                onUse={use}
                busy={busy || Boolean(pendingKey)}
                documentType={documentType}
                error={rowErrors[s.item.key]}
              />
            ))}
          </ul>
          {library.length > DEFAULT_VISIBLE_MATCHES && (
            <button type="button" className="btn-link fm-show-all" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show fewer' : `Show all ${library.length}`}
            </button>
          )}
        </>
      )}
      <div className="fm-footer">
        {confirmPending && (
          <span className="fm-confirm">Press Create anyway to add it as a new custom field.</span>
        )}
        <button type="button" className="btn btn-xs fm-create-anyway" disabled={busy} onClick={onCreateAnyway}>
          Create anyway
        </button>
      </div>
    </div>
  );
}
